import type { OpenCommitmentDto } from '@signalvote/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { useChangeCommitmentExpectBy, useCheckInCommitment, useCloseCommitment, useOpenCommitments } from '@/hooks/queries';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { FundraisingPage } from './FundraisingPage';
import { OpenCommitmentsList } from './OpenCommitmentsList';

/**
 * The Open commitments tab: who committed how much on a call, so the Fundraiser can chase it — logging
 * check-ins and moving the expected date as they go. A commitment stays open until it's closed — by the
 * Fundraiser (received elsewhere, withdrawn or lapsed), or by the system (superseded, logged in error).
 */
const rowOf = (rows: OpenCommitmentDto[], id: string) => rows.find((c) => c.id === id)!;

export function OpenCommitments() {
  const commitments = useOpenCommitments(undefined);
  const { mutate: close, isPending, variables, failure } = useCloseCommitment();
  const { mutateAsync: checkIn } = useCheckInCommitment();
  const { mutateAsync: changeExpectBy } = useChangeCommitmentExpectBy();

  return (
    <FundraisingPage tab="commitments">
      <div className="space-y-6">
        {(commitments.status === 'loading' || commitments.status === 'idle') && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}
        {(commitments.status === 'failed' || commitments.status === 'offline') && (
          <p role="status" className="text-sm text-muted-foreground">
            {commitments.status === 'offline'
              ? "You're offline, so the open commitments can't load."
              : 'We couldn\u2019t load the open commitments right now.'}
          </p>
        )}
        {commitments.status === 'ready' && (
          <OpenCommitmentsList
            commitments={commitments.data}
            onClose={(id, { reason }) => close({ row: rowOf(commitments.data, id), reason })}
            onCheckIn={(id, note) => checkIn({ row: rowOf(commitments.data, id), note })}
            onChangeExpectBy={(id, expectBy) => changeExpectBy({ row: rowOf(commitments.data, id), expectBy })}
            closingId={isPending ? (variables?.row.id ?? null) : null}
            error={failure?.message ?? null}
            today={getCampaignTodayString()}
          />
        )}
      </div>
    </FundraisingPage>
  );
}
